import React, { useEffect, useMemo, useState, memo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, PanelLeftClose } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { prefetchNavRoute } from '../utils/navRoutePrefetch';
import { groupAppNavItems } from '../utils/navigationForBusinessType';
import { isNavPathActive } from '../routing/appRouteCatalog';
import { kvGetRaw, kvSetRaw } from '../infrastructure/storage/kvAccess';

/**
 * App sidebar — navigation + language switcher footer.
 * Must NOT subscribe to Inventory/Sales/Expenses domain slices (ADR-0014 §6 / Phase 2.3).
 * Nav items + unread badge come from Layout (single source of truth).
 */

const ICON_STROKE = 1.75;
const NAV_GROUPS_COLLAPSED_KEY = 'aman_nav_groups_collapsed';

const GROUP_LABEL_KEYS = {
  commerce: 'navGroupCommerce',
  stock: 'navGroupStock',
  finance: 'navGroupFinance',
  admin: 'navGroupAdmin',
};

function readCollapsedGroups() {
  try {
    const raw = kvGetRaw(NAV_GROUPS_COLLAPSED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeCollapsedGroups(map) {
  try {
    kvSetRaw(NAV_GROUPS_COLLAPSED_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

function channelToLinear(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(r, g, b) {
  return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b);
}

/** Keep OS/brand accent readable on the light sidebar rail. */
function contrastSafeSidebarRgb(r, g, b) {
  const L = relativeLuminance(r, g, b);
  // Very light accents wash out on white — darken them.
  if (L > 0.62) {
    const t = 0.52;
    return {
      r: Math.round(r * t),
      g: Math.round(g * t),
      b: Math.round(b * t),
    };
  }
  // Dark accents stay as-is on the light rail (do not brighten / wash).
  return { r, g, b };
}

function applyBrandAccentToDocument(accentHex) {
  if (!accentHex || typeof accentHex !== 'string') return;
  const normalized = accentHex.trim().startsWith('#') ? accentHex.trim() : `#${accentHex.trim()}`;
  const m = normalized.slice(1, 7);
  if (m.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(m)) return;
  let r = parseInt(m.slice(0, 2), 16);
  let g = parseInt(m.slice(2, 4), 16);
  let b = parseInt(m.slice(4, 6), 16);
  ({ r, g, b } = contrastSafeSidebarRgb(r, g, b));
  const toHex = (n) => n.toString(16).padStart(2, '0');
  const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  const root = document.documentElement;
  root.style.setProperty('--sidebar-accent', hex);
  root.style.setProperty('--sidebar-accent-rgb', `${r}, ${g}, ${b}`);
  /* Table headers track the same system accent (see --app-tbl-head-* in optimized-color-system) */
  root.style.setProperty(
    '--app-tbl-head-bg',
    `color-mix(in srgb, ${hex} 11%, var(--ads-color-surface, #ffffff))`
  );
  root.style.setProperty('--app-tbl-head-color', hex);
  root.style.setProperty(
    '--app-tbl-head-border',
    `color-mix(in srgb, ${hex} 24%, var(--ads-color-border, #e5e7eb))`
  );
}

function getAmanBrandHex() {
  if (typeof document === 'undefined') return '';
  const fromToken = getComputedStyle(document.documentElement)
    .getPropertyValue('--ads-color-primary')
    .trim();
  if (fromToken.startsWith('#')) return fromToken;
  const rgbMatch = fromToken.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgbMatch) {
    const toHex = (n) => Math.round(Number(n)).toString(16).padStart(2, '0');
    return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
  }
  return fromToken || '';
}

async function resolveSidebarAccentHex() {
  try {
    const api = typeof window !== 'undefined' ? window.electronAPI : null;
    if (api?.getSystemAccentColor) {
      const res = await api.getSystemAccentColor();
      if (res?.ok && res.accent) return res.accent;
    }
  } catch {
    /* fall through to brand */
  }
  return getAmanBrandHex();
}

const SidebarNavLink = memo(function SidebarNavLink({ path, label, Icon, active, group }) {
  const warm = useCallback(() => prefetchNavRoute(path), [path]);
  return (
    <Link
      to={path}
      className={`app-sidebar-link ${active ? 'is-active' : ''}`}
      data-nav-group={group || undefined}
      title={label}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={warm}
      onFocus={warm}
      onTouchStart={warm}
      onPointerDown={warm}
    >
      {Icon ? (
        <Icon
          className="app-sidebar-link-icon"
          size={20}
          strokeWidth={ICON_STROKE}
          aria-hidden
        />
      ) : null}
      <span className="app-sidebar-link-label">{label}</span>
    </Link>
  );
});

const AppSidebar = memo(function AppSidebar({
  hidden = false,
  onToggleSidebar,
  sidebarExpanded = true,
  compact = false,
  toggleTitle,
  navItems = [],
}) {
  const location = useLocation();
  const { t } = useLanguage();
  const tl = (key) => t(`layoutShell.${key}`);
  const [collapsed, setCollapsed] = useState(readCollapsedGroups);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};

    const apply = (hex) => {
      if (!cancelled && hex) applyBrandAccentToDocument(hex);
    };

    resolveSidebarAccentHex().then(apply);

    try {
      const api = window.electronAPI;
      if (api?.onAccentColorChanged) {
        unsubscribe = api.onAccentColorChanged((accent) => {
          apply(accent || getAmanBrandHex());
        });
      }
    } catch {
      /* ignore */
    }

    return () => {
      cancelled = true;
      try {
        unsubscribe();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const groups = useMemo(() => groupAppNavItems(navItems), [navItems]);
  const flatNavItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const isActive = (path) => isNavPathActive(location.pathname, path);

  const toggleGroup = useCallback((groupKey) => {
    setCollapsed((prev) => {
      const next = { ...prev, [groupKey]: !prev[groupKey] };
      writeCollapsedGroups(next);
      return next;
    });
  }, []);

  if (!navItems.length) return null;

  return (
    <aside
      id="app-sidebar"
      className="app-sidebar ads-sidebar"
      aria-hidden={hidden}
      inert={hidden ? '' : undefined}
      aria-label={tl('navAriaLabel')}
    >
      <div className="app-sidebar-inner">
        <div className="app-sidebar-brand">
          {onToggleSidebar && sidebarExpanded && !hidden && (
            <button
              type="button"
              className="app-sidebar-toggle-btn"
              onClick={onToggleSidebar}
              aria-expanded={sidebarExpanded}
              aria-controls="app-sidebar"
              title={toggleTitle || tl('hideMenu')}
            >
              <PanelLeftClose size={20} strokeWidth={ICON_STROKE} aria-hidden />
            </button>
          )}
          <span className="app-sidebar-brand-text">AMAN ERP</span>
        </div>
        <nav className="app-sidebar-nav">
          {compact
            ? flatNavItems.map((item) => (
                <SidebarNavLink
                  key={item.key}
                  path={item.path}
                  label={item.label}
                  Icon={item.icon}
                  group={item.group}
                  active={isActive(item.path)}
                />
              ))
            : groups.map(({ group, items }) => {
            const labelKey = GROUP_LABEL_KEYS[group];
            const groupLabel = labelKey ? tl(labelKey) : group;
            const isCollapsed = Boolean(collapsed[group]);
            const panelId = `app-sidebar-group-${group}`;
            const hasActive = items.some((item) => isActive(item.path));

            return (
              <div
                key={group}
                className={`app-sidebar-group${isCollapsed ? ' is-collapsed' : ''}${
                  hasActive ? ' has-active' : ''
                }`}
                data-nav-group={group}
              >
                <button
                  type="button"
                  className="app-sidebar-group__toggle"
                  onClick={() => toggleGroup(group)}
                  aria-expanded={!isCollapsed}
                  aria-controls={panelId}
                >
                  <span className="app-sidebar-group__swatch" aria-hidden />
                  <span className="app-sidebar-group__label">{groupLabel}</span>
                  <ChevronDown
                    className="app-sidebar-group__chevron"
                    size={14}
                    strokeWidth={ICON_STROKE}
                    aria-hidden
                  />
                </button>
                <div
                  id={panelId}
                  className="app-sidebar-group__items"
                  role="group"
                  aria-label={groupLabel}
                  aria-hidden={isCollapsed}
                  inert={isCollapsed ? '' : undefined}
                >
                  <div className="app-sidebar-group__items-inner">
                    {items.map((item) => (
                      <SidebarNavLink
                        key={item.key}
                        path={item.path}
                        label={item.label}
                        Icon={item.icon}
                        group={group}
                        active={isActive(item.path)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
        <div className="app-sidebar-footer">
          <LanguageSwitcher variant="sidebar" />
        </div>
      </div>
    </aside>
  );
});

AppSidebar.displayName = 'AppSidebar';

export default AppSidebar;
